import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import { Card, Flex, Select, Space, Typography } from 'antd';
import { PageShell, RefreshButton } from '../components/page-shell.js';
import { RollbackDrainSnapshot } from '../components/rollback-drain-snapshot.js';
import { UnknownOutcomeReviewModal } from '../components/unknown-outcome-review-modal.js';
import type { Configuration, RollbackDrainStatus } from '../types.js';
import { useAdminQuery } from '../use-admin-query.js';

export function RollbackPage() {
  const configuration = useAdminQuery<Configuration>('/admin/v1/configuration');
  const [productId, setProductId] = useState<string>();
  const [operationKey, setOperationKey] = useState<string>();
  const [reviewJobId, setReviewJobId] = useState<string>();

  useEffect(() => {
    if (!productId && configuration.data?.products[0]) {
      setProductId(configuration.data.products[0].id);
    }
  }, [configuration.data, productId]);

  const drainPath = useMemo(() => {
    const parameters = new URLSearchParams();
    if (productId) parameters.set('productId', productId);
    if (operationKey) parameters.set('operationKey', operationKey);
    return `/admin/v1/rollback/drain-status?${parameters.toString()}`;
  }, [operationKey, productId]);
  const drain = useAdminQuery<RollbackDrainStatus>(
    drainPath,
    Boolean(productId),
  );
  const operations = (configuration.data?.operationProfiles ?? [])
    .filter((profile) => profile.productId === productId && profile.isActive)
    .map((profile) => profile.key)
    .filter((key, index, keys) => keys.indexOf(key) === index)
    .sort();

  const error = configuration.error ?? drain.error;
  return (
    <PageShell
      title="Rollback drain"
      description="A fail-closed snapshot of central dispatches, jobs, realtime sessions, and ambiguous provider outcomes. No prompt or response content is displayed."
      action={
        <RefreshButton
          loading={configuration.loading || drain.loading}
          onClick={() => {
            void configuration.refresh();
            if (productId) void drain.refresh();
          }}
        />
      }
      error={error}
    >
      <Card>
        <Flex gap={16} wrap align="end">
          <Selection
            label="Product"
            value={productId ?? null}
            placeholder="Select a product"
            options={(configuration.data?.products ?? []).map((product) => ({
              value: product.id,
              label: product.key,
            }))}
            onChange={(value) => {
              setProductId(value);
              setOperationKey(undefined);
            }}
          />
          <Selection
            allowClear
            label="Operation"
            value={operationKey ?? null}
            placeholder="All operations"
            options={operations.map((key) => ({ value: key, label: key }))}
            onChange={setOperationKey}
          />
          {drain.data ? (
            <Typography.Text type="secondary">
              Snapshot {new Date(drain.data.snapshotAt).toLocaleString()}
            </Typography.Text>
          ) : null}
        </Flex>
      </Card>

      {drain.data ? (
        <RollbackDrainSnapshot
          status={drain.data}
          loading={drain.loading}
          onReview={setReviewJobId}
        />
      ) : null}
      <UnknownOutcomeReviewModal
        jobId={reviewJobId}
        onClose={() => setReviewJobId(undefined)}
        onReviewed={() => {
          setReviewJobId(undefined);
          void drain.refresh();
        }}
      />
    </PageShell>
  );
}

function Selection({
  label,
  ...properties
}: ComponentProps<typeof Select<string>> & { label: string }) {
  return (
    <Space direction="vertical" size={4}>
      <Typography.Text strong>{label}</Typography.Text>
      <Select<string> style={{ minWidth: 240 }} {...properties} />
    </Space>
  );
}
